import { Fragment, ReactNode, useEffect, useRef, useState } from "react";
import { Button, Card, Kbd } from "@lazar/ui";

export interface TabDescriptor {
  id: string;
  label: string;
  render: () => ReactNode;
  // Render the thin divider above this tab in the nav. It travels with the tab,
  // so a conditionally-omitted tab takes its divider with it.
  dividerBefore?: boolean;
  // The "advanced" treatment: a ⚠ affordance plus dimmed-when-inactive styling,
  // used for the destructive Settings → Data pane.
  advanced?: boolean;
}

interface TabModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  // Namespaces the tab/pane/title element ids so two modals can coexist and the
  // aria wiring (aria-controls / aria-labelledby) stays unique per modal.
  idPrefix: string;
  tablistLabel: string;
  closeLabel: string;
  tabs: TabDescriptor[];
  // Extra classes for the scrolling pane — e.g. the analytics pane's tint.
  paneClassName?: string;
  // When set, the body renders this instead of the tablist + pane (and no tabs).
  // Used for the analytics no-entries state, which has no tabs to show.
  emptyState?: ReactNode;
}

// True when a keystroke belongs to the focused form control rather than to a
// global shortcut. The number-key tab hotkeys live on a window listener, so
// without this a digit typed into a Settings field (merge-gap minutes,
// productive-hours, weekly target) would jump tabs instead of landing in the
// input.
const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable
  );
};

// Shared left-nav modal shell behind SettingsModal and the analytics modal: a
// fixed-size centered card with a header, a vertical tablist, and one scrolling
// tabpanel. Owns the chrome and the tab a11y/keyboard wiring; callers supply
// only the tab descriptors and pane content. Presentation only — no store
// access or domain logic lives here.
const TabModal = ({
  isOpen,
  onClose,
  title,
  idPrefix,
  tablistLabel,
  closeLabel,
  tabs,
  paneClassName = "",
  emptyState,
}: TabModalProps) => {
  const firstTabId = tabs[0]?.id;
  const [activeId, setActiveId] = useState(firstTabId);
  // Refs to every rendered tab button, keyed by tab id, so keyboard navigation
  // can move focus onto a tab the user isn't currently on. A button's ref
  // callback deletes its entry on unmount, so conditionally-omitted tabs (e.g.
  // analytics' Smoothing) drop out of the map cleanly.
  const tabRefs = useRef(new Map<string, HTMLButtonElement>());

  // Move selection to a tab and pull focus onto it — the WAI-ARIA "Tabs"
  // pattern's focus-follows-selection model, shared by arrow-key and number-key
  // navigation. Clicks don't need this: the browser focuses the button itself.
  const selectTab = (id: string) => {
    setActiveId(id);
    tabRefs.current.get(id)?.focus();
  };

  // Arrow/Home/End handling for the tablist. Down/Right advance and Up/Left
  // retreat so it works in both layouts — vertical on md+, horizontal on mobile
  // — and movement wraps at the ends. Returns true when it consumed the key, so
  // the caller can preventDefault only then. Indexing into `tabs` means an
  // absent conditional tab is simply never a target.
  const navigateTabs = (currentIndex: number, key: string): boolean => {
    const last = tabs.length - 1;
    let nextIndex: number;
    switch (key) {
      case "ArrowDown":
      case "ArrowRight":
        nextIndex = currentIndex === last ? 0 : currentIndex + 1;
        break;
      case "ArrowUp":
      case "ArrowLeft":
        nextIndex = currentIndex === 0 ? last : currentIndex - 1;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = last;
        break;
      default:
        return false;
    }
    selectTab(tabs[nextIndex].id);
    return true;
  };

  // Reset to the first tab whenever the modal opens so reopening always lands on
  // the default pane. Keyed on the primitive firstTabId — not the tabs array,
  // whose identity changes every render — so switching tabs doesn't reset.
  // activeId already initializes to firstTabId, so this only fires when the set
  // of tabs changes while mounted (e.g. the conditional Smoothing tab appears);
  // that's a prop-driven sync, so set-state-in-effect is disabled deliberately.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (isOpen) setActiveId(firstTabId);
  }, [isOpen, firstTabId]);

  // Move focus onto the first nav tab when the modal opens so keyboard and
  // screen-reader users land inside the dialog rather than on the page behind
  // it. Deferred a tick so it runs after the open-reset above re-renders. On the
  // empty-state branch there's no tablist, so the map is empty and the
  // optional-chained focus is a no-op.
  useEffect(() => {
    if (!isOpen) return;
    const id = setTimeout(() => tabRefs.current.get(firstTabId)?.focus(), 0);
    return () => clearTimeout(id);
  }, [isOpen, firstTabId]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Number-key hotkeys: a bare 1–9 jumps straight to the matching tab, mirroring
  // the stop-timer client picker. Digits are [1-9], so the target is capped at
  // nine for free; an out-of-range or absent tab just isn't a target. Skipped
  // when a modifier is held or focus sits in a form control (see
  // isEditableTarget), and on the tab-less empty-state branch.
  useEffect(() => {
    if (!isOpen || emptyState !== undefined) return;
    const handleDigit = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditableTarget(e.target)) return;
      if (!/^[1-9]$/.test(e.key)) return;
      const index = parseInt(e.key, 10) - 1;
      if (index >= tabs.length) return;
      e.preventDefault();
      selectTab(tabs[index].id);
    };
    window.addEventListener("keydown", handleDigit);
    return () => window.removeEventListener("keydown", handleDigit);
  }, [isOpen, emptyState, tabs]);

  if (!isOpen) return null;

  // Fall back to the first tab if the active id points at a tab that isn't
  // present (e.g. a conditional tab the caller omitted while it was selected).
  const activeTab = tabs.find((t) => t.id === activeId) ?? tabs[0];
  const titleId = `${idPrefix}-modal-title`;
  const paneId = `${idPrefix}-pane`;

  return (
    <div
      className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <Card
        className="w-full max-w-5xl h-[85vh] flex flex-col p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b-[3px] border-ink dark:border-cream px-6 py-4 shrink-0">
          <h2 id={titleId} className="text-lg font-black dark:text-cream">
            {title}
          </h2>
          <Button onClick={onClose} aria-label={closeLabel}>
            Close <Kbd>Esc</Kbd>
          </Button>
        </header>

        {emptyState !== undefined ? (
          <div className="flex flex-col md:flex-row flex-1 min-h-0">
            <div className={`flex-1 overflow-y-auto p-6 ${paneClassName}`}>
              {emptyState}
            </div>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row flex-1 min-h-0">
            <div
              role="tablist"
              aria-label={tablistLabel}
              aria-orientation="vertical"
              className="flex md:flex-col gap-1 p-3 shrink-0 overflow-x-auto border-b-[3px] md:border-b-0 md:border-r-[3px] border-ink dark:border-cream md:w-52"
            >
              {tabs.map((tab, index) => {
                const active = activeTab?.id === tab.id;
                return (
                  <Fragment key={tab.id}>
                    {tab.dividerBefore && (
                      <div
                        className="hidden md:block brut-divider-thin my-2"
                        aria-hidden="true"
                      />
                    )}
                    <Button
                      ref={(el) => {
                        if (el) tabRefs.current.set(tab.id, el);
                        else tabRefs.current.delete(tab.id);
                      }}
                      id={`${idPrefix}-tab-${tab.id}`}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      aria-controls={paneId}
                      // Roving tabindex: only the active tab is in the page tab
                      // order, so Tab moves into/out of the tablist as one stop
                      // and arrow keys move between tabs within it.
                      tabIndex={active ? 0 : -1}
                      onClick={() => setActiveId(tab.id)}
                      onKeyDown={(e) => {
                        if (navigateTabs(index, e.key)) e.preventDefault();
                      }}
                      variant={active ? "primary" : "default"}
                      className={`md:w-full justify-start shrink-0 ${
                        tab.advanced && !active ? "opacity-70" : ""
                      }`}
                    >
                      <span className="flex-1 text-left">{tab.label}</span>
                      {index < 9 && (
                        <Kbd
                          aria-label={`Press ${index + 1} to open this section`}
                        >
                          {index + 1}
                        </Kbd>
                      )}
                      {tab.advanced && (
                        <span
                          aria-hidden="true"
                          className="text-coral-600 dark:text-coral-500"
                        >
                          ⚠
                        </span>
                      )}
                    </Button>
                  </Fragment>
                );
              })}
            </div>

            <div
              id={paneId}
              role="tabpanel"
              aria-labelledby={
                activeTab ? `${idPrefix}-tab-${activeTab.id}` : undefined
              }
              className={`flex-1 overflow-y-auto p-6 ${paneClassName}`}
            >
              {activeTab?.render()}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default TabModal;
